import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/sonner';
import { useTenantCreationRequest } from './use-tenant-creation-request';
import {
  buildPatchTenantCreationRequestFormData,
  PatchTenantCreationRequestData,
} from '@/lib/build-patch-tenant-creation-request-form-data';

const API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL;

export function usePatchTenantCreationRequest() {
  const { slug } = useTenantCreationRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PatchTenantCreationRequestData) => {
      const formData = buildPatchTenantCreationRequestFormData(data);

      const response = await fetch(
        `${API_BASE_URL}/tenant-creation-requests/${slug}`,
        {
          method: 'PATCH',
          body: formData,
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ message: 'Unknown error' }));
        throw new Error(error.message || 'Failed to create tenant request');
      }
    },
    onSuccess: async () => {
      // `['tenant-creation-requests', slug]` is a prefix match of
      // `['tenant-creation-requests']`, so invalidating the broader key
      // already covers both the detail query and any list view — a second,
      // separately-awaited invalidateQueries() for the slug-scoped key was
      // just re-triggering (and re-waiting on) a refetch of the exact same
      // query, doubling the network round trip on every patch across the
      // entire onboarding/signup funnel.
      await queryClient.invalidateQueries({
        queryKey: ['tenant-creation-requests'],
      });
    },
    onError: (error: Error) => {
      toast.error('Error', {
        description: error.message,
      });
    },
  });
}
